/**
 * The websocket behind the Support Chat console: ant's GraphQL subscriptions
 * over Phoenix channels (Absinthe's "__absinthe__:control" channel, not
 * graphql-ws), through the official clients "phoenix" and "@absinthe/socket".
 *
 * One Phoenix socket per admin token, opened the first time something
 * subscribes and closed by closeChatSocket() when the page unmounts; a
 * different token (re-login) gets a fresh socket. ant refuses the handshake
 * without a valid token. The token travels as Phoenix 1.8's `authToken`
 * (the Sec-WebSocket-Protocol header), not in the URL, where proxies and
 * access logs would keep it; node_modules/phoenix is 1.8, so no URL-param
 * fallback is kept. Phoenix reconnects by itself with backoff after an
 * abnormal close, but NOT after a close with code 1000 (ant closing the
 * connection cleanly, e.g. on a deploy): Phoenix 1.8 treats that as final.
 * This module reconnects in that case itself, with its own backoff, as long
 * as the socket is still the current one. Either way, on rejoin
 * @absinthe/socket re-sends every subscription still wanted, so a
 * subscription made here survives a dropped connection. What ant published
 * while the socket was down is gone, which is why useChat re-fetches whenever
 * it goes live again.
 *
 * Every subscription document is verified against ant's SDL
 * (priv/graphql/schema.graphql, RootSubscriptionType).
 */

import * as withAbsintheSocket from '@absinthe/socket';
import { Socket as PhoenixSocket } from 'phoenix';
import { getCredentials } from '../../api/http';
import { getGraphQLEndpoint } from '../../api/graphql/endpoints';
import { AntChatMessage, AntChatSession, MESSAGE_FIELDS, SESSION_FIELDS } from './api';

export type ChatSocketState = 'connecting' | 'open' | 'closed';

/** Exactly one of the two is set: `created` on open, `updated` on any later change. */
export interface ChatSessionEvent {
  created: AntChatSession | null;
  updated: AntChatSession | null;
}

/** How long to wait before re-sending a subscription ant rejected or that timed out. */
const RESUBSCRIBE_MS = 5000;

/** WebSocket close code for a normal, clean close. */
const CLOSE_NORMAL = 1000;

/** Waits before reconnecting after a clean close, by consecutive attempt. */
const CLEAN_CLOSE_RECONNECT_MS = [1000, 2000, 5000, 10000, 30000];

/** The wait before the `attempt`-th reconnect (0-based) after a clean close. */
export function cleanCloseReconnectDelay(attempt: number): number {
  return CLEAN_CLOSE_RECONNECT_MS[Math.min(attempt, CLEAN_CLOSE_RECONNECT_MS.length - 1)];
}

/** The websocket next to the GraphQL endpoint: https://host/v1/gql → wss://host/v1/ws/gql. */
export function chatSocketUrl(graphqlEndpoint: string): string {
  return graphqlEndpoint.replace(/^http/, 'ws').replace(/\/gql\/?$/, '/ws/gql');
}

// ============================================================================
// Documents
// ============================================================================

const SESSION_UPDATED_SUBSCRIPTION = `
  subscription SupportChatSessionUpdated {
    chatSessionUpdated {
      created {
        ...ChatSessionFields
      }
      updated {
        ...ChatSessionFields
      }
    }
  }
  ${SESSION_FIELDS}
`;

const MESSAGE_SENT_SUBSCRIPTION = `
  subscription SupportChatMessageSent($conversationId: ID) {
    chatMessageSent(conversationId: $conversationId) {
      created {
        ...ChatMessageFields
      }
    }
  }
  ${MESSAGE_FIELDS}
`;

/** Someone typing in a conversation (ant's ChatTypingEvent). Never stored. */
export interface ChatTypingEvent {
  conversationId: string;
  /** "user" for the rider, "admin" for an agent. */
  senderType: string;
  senderId: string;
  /** The rider's first name, or the agent's first name and last initial. */
  name: string | null;
  at: string;
}

const TYPING_SUBSCRIPTION = `
  subscription SupportChatTyping($conversationId: ID!) {
    chatTyping(conversationId: $conversationId) {
      conversationId
      senderType
      senderId
      name
      at
    }
  }
`;

// ============================================================================
// The socket
// ============================================================================

interface ChatSocket {
  token: string;
  phoenix: PhoenixSocket;
  absinthe: withAbsintheSocket.AbsintheSocket;
  /** A reconnect scheduled after a clean close, if any. */
  reconnectTimer: number | null;
  /** Reconnects after a clean close since the socket was last open, for the backoff. */
  reconnectAttempts: number;
}

let current: ChatSocket | null = null;
let state: ChatSocketState = 'closed';
const stateListeners = new Set<(state: ChatSocketState) => void>();

function setState(next: ChatSocketState) {
  if (state === next) return;
  state = next;
  stateListeners.forEach((listener) => listener(next));
}

export function getChatSocketState(): ChatSocketState {
  return state;
}

/** Called on every change of the socket's state. Returns the unsubscribe function. */
export function onChatSocketState(
  listener: (state: ChatSocketState) => void
): () => void {
  stateListeners.add(listener);
  return () => {
    stateListeners.delete(listener);
  };
}

/**
 * Reconnects `socket` after a clean close, after the backoff delay, unless
 * it has been replaced or closed meanwhile or Phoenix is already on it.
 */
function reconnectAfterCleanClose(socket: ChatSocket) {
  if (socket.reconnectTimer !== null) return;
  const delay = cleanCloseReconnectDelay(socket.reconnectAttempts);
  socket.reconnectAttempts += 1;
  socket.reconnectTimer = window.setTimeout(() => {
    socket.reconnectTimer = null;
    if (current !== socket) return;
    const connection = socket.phoenix.connectionState();
    if (connection === 'open' || connection === 'connecting') return;
    // connect() is a no-op while Phoenix still holds the closed transport,
    // so let it drop that one first.
    socket.phoenix.disconnect(() => {
      if (current === socket) socket.phoenix.connect();
    });
  }, delay);
}

/**
 * The socket for the current admin token, opened on first use. Null while
 * there is no token (signed out), so nothing connects without one.
 */
function getChatSocket(): ChatSocket | null {
  const { graphqlToken } = getCredentials();
  if (!graphqlToken) return null;
  if (current && current.token === graphqlToken) return current;
  if (current) closeChatSocket();

  const phoenix = new PhoenixSocket(chatSocketUrl(getGraphQLEndpoint()), {
    authToken: graphqlToken,
  });
  const socket: ChatSocket = {
    token: graphqlToken,
    phoenix,
    absinthe: withAbsintheSocket.create(phoenix),
    reconnectTimer: null,
    reconnectAttempts: 0,
  };
  // A socket replaced by a newer one must not report its own closing.
  const isCurrent = () => current === socket;
  phoenix.onOpen(() => {
    if (!isCurrent()) return;
    socket.reconnectAttempts = 0;
    setState('open');
  });
  phoenix.onClose((event) => {
    if (!isCurrent()) return;
    setState('connecting');
    // Phoenix reconnects after any other close; after a clean one, we do.
    if (event?.code === CLOSE_NORMAL) reconnectAfterCleanClose(socket);
  });
  current = socket;
  setState('connecting');
  phoenix.connect();
  return socket;
}

/** Closes the socket, if any. Subscriptions still wanted reopen it on their next attempt. */
export function closeChatSocket(): void {
  const socket = current;
  current = null;
  if (socket) {
    if (socket.reconnectTimer !== null) window.clearTimeout(socket.reconnectTimer);
    socket.reconnectTimer = null;
    socket.phoenix.disconnect();
  }
  setState('closed');
}

// ============================================================================
// Subscriptions
// ============================================================================

interface SubscriptionHandlers<TData> {
  onData: (data: TData) => void;
  /**
   * True once ant acknowledged the subscription, false when it is lost: the
   * connection dropped (it comes back on rejoin), ant rejected the document or
   * the request timed out (re-sent after a pause).
   */
  onStatus?: (active: boolean) => void;
}

interface SubscriptionResult<TData> {
  data?: TData | null;
  errors?: Array<{ message?: string }>;
}

/** Errors @absinthe/socket raises while the notifier stays alive and will be re-sent on rejoin. */
const isTransientError = (error: Error): boolean =>
  /^(connection: close|channel join:)/.test(error.message);

function subscribe<TData>(
  operation: string,
  variables: Record<string, unknown>,
  handlers: SubscriptionHandlers<TData>
): () => void {
  // Rebuilt on every start(): @absinthe/socket keys its notifiers by request
  // object, and a doc push that timed out leaves the old one behind as
  // "sending". Re-sending with the same object would be a no-op forever.
  let request = { operation, variables };
  let wanted = true;
  let socket: ChatSocket | null = null;
  let notifier: withAbsintheSocket.Notifier | null = null;
  let observer: withAbsintheSocket.Observer | null = null;
  let retry: number | null = null;

  const setActive = (active: boolean) => {
    if (wanted && handlers.onStatus) handlers.onStatus(active);
  };

  /**
   * The notifier as @absinthe/socket currently holds it. The library replaces
   * the object on every status change, so cancelling with the one `send`
   * returned would skip the unsubscribe push once the subscription is live.
   */
  const liveNotifier = (): withAbsintheSocket.Notifier | null =>
    socket?.absinthe.notifiers.find((n) => n.request === request) ?? notifier;

  const stop = () => {
    const live = liveNotifier();
    if (socket && live && observer) {
      try {
        withAbsintheSocket.unobserveOrCancel(socket.absinthe, live, observer);
      } catch {
        // The library already detached this observer (it does on some socket
        // error paths) and says so by throwing. stop() runs from React
        // cleanups and the resubscribe timer, where a throw takes the page
        // down or kills the retry, so cancel the orphan here instead: ant
        // still gets its unsubscribe.
        if (live.activeObservers.length === 0) {
          withAbsintheSocket.cancel(socket.absinthe, live);
        }
      }
    }
    notifier = null;
    observer = null;
  };

  const restartLater = () => {
    if (!wanted || retry !== null) return;
    retry = window.setTimeout(() => {
      retry = null;
      stop();
      start();
    }, RESUBSCRIBE_MS);
  };

  const start = () => {
    if (!wanted) return;
    socket = getChatSocket();
    if (!socket) return;
    observer = {
      onStart: () => setActive(true),
      onResult: (result: SubscriptionResult<TData>) => {
        if (result?.data) {
          handlers.onData(result.data);
        } else if (result?.errors?.length) {
          console.warn('[chat socket] subscription result carried errors', result.errors);
        }
      },
      onError: (error: Error) => {
        setActive(false);
        if (!isTransientError(error)) {
          console.warn('[chat socket] subscription error, re-sending shortly', error);
          restartLater();
        }
      },
      onAbort: (error: Error) => {
        setActive(false);
        console.warn('[chat socket] subscription aborted, re-sending shortly', error);
        notifier = null;
        observer = null;
        restartLater();
      },
    };
    request = { operation, variables };
    notifier = withAbsintheSocket.send(socket.absinthe, request);
    withAbsintheSocket.observe(socket.absinthe, notifier, observer);
  };

  start();

  return () => {
    wanted = false;
    if (retry !== null) {
      window.clearTimeout(retry);
      retry = null;
    }
    stop();
  };
}

/**
 * Every session the token may see, as it opens (`created`) and as it is
 * anchored, assigned, prioritised, tagged or closed (`updated`). A new
 * message does not fire this: the message itself is the event.
 */
export function subscribeSessions(
  onEvent: (event: ChatSessionEvent) => void,
  onStatus?: (active: boolean) => void
): () => void {
  return subscribe<{ chatSessionUpdated: ChatSessionEvent | null }>(
    SESSION_UPDATED_SUBSCRIPTION,
    {},
    {
      onData: (data) => {
        if (data.chatSessionUpdated) onEvent(data.chatSessionUpdated);
      },
      onStatus,
    }
  );
}

/**
 * Every message written to one conversation, or to any conversation the
 * token may read when `conversationId` is null (rider messages, admin
 * replies, internal notes, the assistant). A resend with the same
 * clientMessageId publishes the same message again: callers dedupe by id.
 */
export function subscribeMessages(
  conversationId: string | null,
  onCreated: (message: AntChatMessage) => void,
  onStatus?: (active: boolean) => void
): () => void {
  return subscribe<{ chatMessageSent: { created: AntChatMessage | null } | null }>(
    MESSAGE_SENT_SUBSCRIPTION,
    conversationId ? { conversationId } : {},
    {
      onData: (data) => {
        const created = data.chatMessageSent?.created;
        if (created) onCreated(created);
      },
      onStatus,
    }
  );
}

/**
 * Who is typing in one conversation (ant's CONVERSATION id), the caller's
 * own events included: callers filter on senderId. ant refuses a
 * conversation the token may not read with "could not be found", which is
 * re-sent after a pause like any rejected subscription.
 */
export function subscribeTyping(
  conversationId: string,
  onTyping: (event: ChatTypingEvent) => void,
  onStatus?: (active: boolean) => void
): () => void {
  return subscribe<{ chatTyping: ChatTypingEvent | null }>(
    TYPING_SUBSCRIPTION,
    { conversationId },
    {
      onData: (data) => {
        if (data.chatTyping) onTyping(data.chatTyping);
      },
      onStatus,
    }
  );
}
